import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Search, Plus, Users, Car, FileText, Phone, Mail, MessageCircle, AlertTriangle,
  MapPin, RefreshCw, Pencil, Trash2, Camera, Upload,
} from "lucide-react";
import { money, money2, daysLeft, calcCommission, effectiveRate } from "../lib/finance";
import { API } from "../lib/apiClient";
import { waTarget } from "../lib/wa";
import { PageTitle, Empty, Modal } from "./common";
import { VehicleDetail } from "./VehicleDetail";
import { normalizePlate, isValidPlate, plateToProvince } from "../lib/plate";

export function Vehicles({ policies, customers, branches, settings, vehicles = [], query, setQuery, setEditingPolicy, setEditingVehicle, setModal, setFollowUpPolicy, deleteVehicle }) {
  const [selectedPlate, setSelectedPlate] = useState(null);

  // Group policies by normalized plate + merge with manual vehicle inventory
  const groups = useMemo(() => {
    const map = {};
    // Start with manual vehicles
    vehicles.forEach((v) => {
      const plate = normalizePlate(v.plate);
      if (!plate) return;
      if (!map[plate]) map[plate] = { plate, policies: [], customers: new Set(), branches: new Set(), companies: new Set(), manualId: v.id, brand: v.brand, model: v.model, year: v.year, note: v.note, ruhsatSeriNo: v.ruhsatSeriNo || "", inspectionLast: v.inspectionLast || "", inspectionEnd: v.inspectionEnd || "" };
      if (v.customer) map[plate].customers.add(v.customer);
    });
    // Then policies
    policies.forEach((p) => {
      const plate = normalizePlate(p.plate);
      if (!plate) return;
      if (!map[plate]) map[plate] = { plate, policies: [], customers: new Set(), branches: new Set(), companies: new Set() };
      map[plate].policies.push(p);
      if (p.customer) map[plate].customers.add(p.customer);
      if (p.branch) map[plate].branches.add(p.branch);
      if (p.company) map[plate].companies.add(p.company);
    });
    return Object.values(map)
      .map((g) => {
        g.policies.sort((a, b) => (b.end || "").localeCompare(a.end || ""));
        const totalPremium = g.policies.reduce((s, p) => s + Number(p.premium || 0), 0);
        const latestEnd = g.policies[0]?.end || "";
        const totalPersonal = g.policies.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
        const today = new Date().toISOString().slice(0, 10);
        const activeBranches = new Set(g.policies.filter((p) => p.start <= today && p.end >= today).map((p) => p.branch));
        const missingCoverage = [];
        if (!activeBranches.has("Trafik")) missingCoverage.push("Trafik");
        if (!activeBranches.has("Kasko")) missingCoverage.push("Kasko");
        return { ...g, totalPremium, totalPersonal, latestEnd, missingCoverage, activeBranches: Array.from(activeBranches) };
      })
      .sort((a, b) => (b.latestEnd || "").localeCompare(a.latestEnd || ""));
  }, [policies, vehicles, branches, settings]);

  const filtered = query
    ? groups.filter((g) => `${g.plate} ${Array.from(g.customers).join(" ")} ${g.brand || ""} ${g.model || ""}`.toLocaleLowerCase("tr").includes(query.toLocaleLowerCase("tr")))
    : groups;

  const selected = selectedPlate ? groups.find((g) => g.plate === selectedPlate) : null;
  const totalManual = vehicles.length;

  const openNewVehicle = () => { setEditingVehicle(null); setModal("vehicle"); };
  const openEditManualVehicle = (g) => { setEditingVehicle({ id: g.manualId || null, plate: g.plate, customer: Array.from(g.customers)[0] || "", brand: g.brand || "", model: g.model || "", year: g.year || "", note: g.note || "", ruhsatSeriNo: g.ruhsatSeriNo || "", inspectionLast: g.inspectionLast || "", inspectionEnd: g.inspectionEnd || "" }); setModal("vehicle"); };

  return (
    <>
      <PageTitle
        eyebrow="ARAÇ PORTFÖYÜ"
        title="Araçlar"
        description={`${groups.length} plaka · ${policies.filter((p) => normalizePlate(p.plate)).length} poliçe · ${totalManual} manuel araç`}
        action="Yeni araç"
        actionTest="new-vehicle-button"
        onAction={openNewVehicle}
      />
      <div className="toolbar">
        <div className="inline-search">
          <Search size={15} />
          <input data-testid="vehicle-search" placeholder="Plaka, müşteri, marka veya model ara..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>
      {filtered.length === 0 ? (
        <Empty text="Plaka bilgisi olan poliçe veya manuel araç yok. 'Yeni araç' ile envantere ekleyebilirsiniz." />
      ) : (
        <div className="vehicle-grid" data-testid="vehicle-grid">
          {filtered.map((g) => {
            const noPolicies = g.policies.length === 0;
            const brandModel = [g.brand, g.model, g.year].filter(Boolean).join(" ");
            return (
              <button
                key={g.plate}
                className={`vehicle-card ${noPolicies ? "no-policy" : ""}`}
                data-testid={`vehicle-card-${g.plate.replace(/\s/g, "-")}`}
                onClick={() => setSelectedPlate(g.plate)}
              >
                <div className="vehicle-plate-badge">
                  <span className="plate-country">TR</span>
                  <span className="plate-number">{g.plate}</span>
                </div>
                {g.ruhsatSeriNo && <div className="vehicle-ruhsat-chip" data-testid={`vehicle-ruhsat-${g.plate.replace(/\s/g, "-")}`} title="Ruhsat Seri No">RSN: {g.ruhsatSeriNo}</div>}
                {brandModel && <div className="vehicle-brand" data-testid={`vehicle-brand-${g.plate.replace(/\s/g, "-")}`}>{brandModel}</div>}
                <div className="vehicle-owner">
                  <Users size={12} />
                  {Array.from(g.customers).slice(0, 2).join(", ") || <em>Sahibi belirtilmedi</em>}
                  {g.customers.size > 2 && ` +${g.customers.size - 2}`}
                </div>
                <div className="vehicle-stats">
                  <div><strong>{g.policies.length}</strong><span>Poliçe</span></div>
                  <div><strong>{money(g.totalPremium)}</strong><span>Toplam prim</span></div>
                  <div><strong>{Array.from(g.branches).length || (noPolicies ? "—" : 0)}</strong><span>Branş</span></div>
                </div>
                <div className="vehicle-tags">
                  {noPolicies && <span className="vehicle-tag manual">📋 Envanterde · Poliçesiz</span>}
                  {g.activeBranches.map((b) => <span key={b} className="vehicle-tag active">{b}</span>)}
                  {!noPolicies && g.missingCoverage.map((b) => <span key={b} className="vehicle-tag missing" title={`${b} teminatı yok/bitmiş`}>⚠ {b} eksik</span>)}
                  {g.inspectionEnd && (() => {
                    const dl = daysLeft(g.inspectionEnd);
                    if (dl < 0) return <span className="vehicle-tag inspection-overdue" data-testid={`inspection-tag-${g.plate.replace(/\s/g, "-")}`} title={`Muayene ${Math.abs(dl)} gün önce bitti`}>🛠️ Muayene {Math.abs(dl)}g gecikti</span>;
                    if (dl <= 30) return <span className="vehicle-tag inspection-soon" data-testid={`inspection-tag-${g.plate.replace(/\s/g, "-")}`} title={`Muayene ${dl} gün sonra`}>🛠️ Muayene {dl}g</span>;
                    if (dl <= 60) return <span className="vehicle-tag inspection-ok" data-testid={`inspection-tag-${g.plate.replace(/\s/g, "-")}`} title={`Muayene ${dl} gün sonra`}>🛠️ Muayene {dl}g</span>;
                    return null;
                  })()}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {selected && (
        <VehicleDetail
          group={selected}
          branches={branches}
          settings={settings}
          onClose={() => setSelectedPlate(null)}
          onEditPolicy={(p) => { setEditingPolicy(p); setModal("policy"); setSelectedPlate(null); }}
          onFollowUp={(p) => { setFollowUpPolicy(p); setModal("followup"); setSelectedPlate(null); }}
          onEditManual={() => { openEditManualVehicle(selected); setSelectedPlate(null); }}
          onDeleteManual={selected.manualId && deleteVehicle ? () => { deleteVehicle(selected.manualId); setSelectedPlate(null); } : null}
          onAddPolicy={() => {
            const stub = {
              plate: selected.plate,
              customer: Array.from(selected.customers)[0] || "",
              branch: "Trafik",
              company: "",
              policeNo: "",
              start: new Date().toISOString().slice(0, 10),
              end: "",
              premium: "",
              commissionRate: "",
              payment: "Bekliyor",
              note: "",
            };
            setEditingPolicy(stub);
            setModal("policy");
            setSelectedPlate(null);
          }}
        />
      )}
    </>
  );
}

